import {
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  SimpleChanges
} from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'

export interface AutocompleteOption {
  id: any
  label: string
}

export interface AutocompleteGroup {
  name: string
  options: AutocompleteOption[]
}

// Champ de sélection unique avec autocomplétion (typeahead). Options groupées et
// filtrées à la saisie (insensible à la casse et aux accents). Implémente
// ControlValueAccessor : la valeur du form control est l'`id` de l'option choisie
// (ou null). Même esprit que <multiselect>, mais sélection unique + recherche.
@Component({
  selector: 'autocomplete-select',
  templateUrl: './autocomplete-select.component.html',
  styleUrls: ['./autocomplete-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteSelectComponent),
      multi: true
    }
  ]
})
export class AutocompleteSelectComponent
  implements ControlValueAccessor, OnChanges {
  @Input() groups: AutocompleteGroup[] = []
  @Input() placeholder = 'Rechercher...'

  // Texte affiché dans l'input : soit le libellé de la valeur sélectionnée,
  // soit la recherche en cours de saisie.
  query = ''
  isOpen = false
  isDisabled = false

  // true dès que l'utilisateur tape : on filtre alors sur `query`. false quand
  // `query` reflète simplement le libellé de la valeur sélectionnée.
  private isFiltering = false
  private selectedId: any = null

  onChange: any = () => {}
  onTouched: any = () => {}

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Les groupes peuvent arriver après writeValue (chargement asynchrone du
    // prélèvement) : on resynchronise le libellé affiché tant que l'utilisateur
    // ne filtre pas.
    if (changes.groups && !this.isFiltering) {
      this.query = this.labelFor(this.selectedId)
    }
  }

  // Groupes filtrés selon la saisie ; les groupes vides sont masqués.
  get filteredGroups(): AutocompleteGroup[] {
    if (!this.isFiltering || !this.query) {
      return this.groups || []
    }
    const needle: string = this.normalize(this.query)
    return (this.groups || [])
      .map((group: AutocompleteGroup) => ({
        name: group.name,
        options: group.options.filter(
          (option: AutocompleteOption) =>
            this.normalize(option.label).indexOf(needle) !== -1
        )
      }))
      .filter((group: AutocompleteGroup) => group.options.length > 0)
  }

  get hasResults(): boolean {
    return this.filteredGroups.some(group => group.options.length > 0)
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.close()
    }
  }

  writeValue(value: any): void {
    this.selectedId = value == null ? null : value
    this.isFiltering = false
    this.query = this.labelFor(this.selectedId)
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState?(isDisabled: boolean): void {
    this.isDisabled = isDisabled
  }

  onFocus(): void {
    if (this.isDisabled) {
      return
    }
    // À la réouverture on repart d'une liste complète tant que rien n'est tapé.
    this.isFiltering = false
    this.isOpen = true
  }

  onInput(value: string): void {
    this.query = value
    this.isFiltering = true
    this.isOpen = true
  }

  select(option: AutocompleteOption): void {
    this.selectedId = option.id
    this.query = option.label
    this.isFiltering = false
    this.isOpen = false
    this.onChange(option.id)
    this.onTouched()
  }

  // Entrée : sélectionne le premier résultat filtré (raccourci clavier).
  onEnter(): void {
    if (!this.isFiltering) {
      return
    }
    const group = this.filteredGroups.find(g => g.options.length > 0)
    if (group) {
      this.select(group.options[0])
    }
  }

  close(): void {
    this.isOpen = false
    // Si la saisie ne correspond pas à la sélection courante, on restaure le
    // libellé sélectionné pour ne pas laisser un texte sans valeur associée.
    this.isFiltering = false
    this.query = this.labelFor(this.selectedId)
  }

  private labelFor(id: any): string {
    if (id == null) {
      return ''
    }
    for (const group of this.groups || []) {
      const match = group.options.find(
        (option: AutocompleteOption) => String(option.id) === String(id)
      )
      if (match) {
        return match.label
      }
    }
    return ''
  }

  // Minuscule + suppression des diacritiques (accents) pour une recherche tolérante.
  private normalize(text: string): string {
    return (text || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
  }
}
